import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpParams, HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { SalesDashboardService } from '../../../../Services/SalesDashboard/sales-dashboard.service';
import { FirmService } from '../../../../Services/firm.service';
import { InvoiceService } from '../../../../Services/Invoice/invoice.service';
import { CategoryService } from '../../../../Services/Category/category.service';
import { ExpenseService } from '../../../../Services/Expenses/expense.service';

@Component({
  selector: 'app-sales-dashboard',
  standalone: true,
  imports: [RouterLink, CommonModule, HttpClientModule, FormsModule],
  templateUrl: './sales-dashboard.component.html',
  styleUrls: ['./sales-dashboard.component.css']
})
export class SalesDashboardComponent implements OnInit {
  // --- DATA ARRAYS (Maintained) ---
  allInvoices: any[] = [];
  firms: any[] = [];
  readyProducts: any[] = [];
  readyCategories: any[] = [];
  assemblyCategory: any[] = [];
  assemblyProducts: any[] = [];
  salesExecutives: any[] = [];
  topFourCustomers: any[] = [];
  topTenCustomers: any[] = [];
  expenses: any[] = [];
  staffWiseExpenses: any[] = [];
  activeFilter: string = '';

  // --- 🔹 FINANCIAL & METAL RECONCILIATION PROPS (Mapped to JSON Keys) ---
  totalAmountBilled: number = 0;   // total_amount (Bill + Udhar)
  totalAmountReceived: number = 0; // amount_paid
  totalAmountDue: number = 0;      // balance_due
  totalMetalBilled: number = 0;    // total_silver_wt
  totalMetalReceived: number = 0;  // received_metal
  totalMetalSettled: number = 0;   // fixed_metal_delta (converted to cash)
  totalMetalDue: number = 0;       // balance_silver_wt

  // --- 🔹 KPI & BREAKDOWN PROPS ---
  totalReadyAmt: any = { total_amount_paid: 0 };
  totalReadyProductSale: number = 0;
  totalAssemblyAmt: number = 0;
  total_expense: any = 0;
  lotWiseSale: number = 0;
  pieceWiseSale: number = 0;
  productTypeSale: number = 0;

  // --- 🔹 VISUALS & DYNAMIC TABLES ---
  lotPercent: number = 0;
  piecePercent: number = 0;
  productPercent: number = 0;
  dynamicReadyCategoryItems: any[] = [];
  dynamicAssemblyCategoryItems: any[] = [];

  // --- 🔹 STAFF PERFORMANCE & PAGINATION ---
  staffPerformance: any[] = [];
  currentPageStaff: number = 1;
  pageSizeStaff: number = 5;

  // --- FILTER MODELS ---
  selectedFirmId: number | null = null;
  dateFrom: string = '';
  dateTo: string = '';

  constructor(
    private router: Router,
    private salesDashboardService: SalesDashboardService,
    private firmService: FirmService,
    private invoiceService: InvoiceService,
    private categoryService: CategoryService,
    private expenseService: ExpenseService
  ) { }

  ngOnInit() {
    this.getFirms();
    this.getInvoiceData();
    this.refreshAllData();
    this.getCategories();
  }

  // 1. GET RAW INVOICES & DATA
  getInvoiceData() {
    this.invoiceService.getInvoices().subscribe((res: any) => {
      this.allInvoices = Array.isArray(res) ? res : (res.data || []);
      this.applyCalculations();
    });
  }

  getCategories() {
    this.categoryService.getCategories().subscribe((res: any) => {
      const newCategories = res.data;
      this.readyCategories = newCategories.filter((c: any) => c.Type === 'Master');
      this.assemblyCategory = newCategories.filter((c: any) => c.Type === 'Assembly');
    });
  }

  // 2. FILTER & REFRESH LOGIC
  refreshAllData() {
    let params = new HttpParams();
    if (this.selectedFirmId) params = params.set('firm_id', this.selectedFirmId.toString());
    params = params.set('from_date', this.dateFrom);
    params = params.set('to_date', this.dateTo);

    this.getTotalReadyProductAmount(params);
    this.getAssemblyLineSale(params);
    this.getTotalReadyProductSale(params);
    this.getTotalExpenses(params);
    this.getTopTenCustomers(params);
    this.fetchNewCategories(params);
    this.fetchSalesExecutiveReport(params);
    this.getStaffWiseExpense(params)

    this.applyCalculations();
  }

  onFirmChange(event: Event) {
    this.selectedFirmId = Number((event.target as HTMLSelectElement).value) || null;
    this.refreshAllData();
  }

  clearFilters() {
    this.selectedFirmId = null;
    this.dateFrom = '';
    this.dateTo = '';
    this.refreshAllData();
  }

  // 3. FRONTEND CALCULATION ENGINE
  applyCalculations() {
    let billedVal = 0; let receivedVal = 0; let dueVal = 0;
    let metBilled = 0; let metRecv = 0; let metDue = 0; let metSettled = 0;
    let readyTotal = 0; let assemblyTotal = 0;
    let lotTotal = 0; let pieceTotal = 0; let productOnlyTotal = 0;
    let currentTransactionRev = 0;

    const readyMap = new Map();
    const assemblyMap = new Map();
    const perfMap = new Map();

    const filtered = this.allInvoices.filter(inv => {
      const matchFirm = !this.selectedFirmId || inv.firm_id == this.selectedFirmId || inv.stout_firm_id == this.selectedFirmId;
      const matchDate = (!this.dateFrom || inv.invoice_date >= this.dateFrom) && (!this.dateTo || inv.invoice_date <= this.dateTo);
      return matchFirm && matchDate;
    });

    filtered.forEach(inv => {
      // Financial Reconciliation (Based on total bill vs current payments)
      billedVal += parseFloat(inv.total_amount || 0);
      receivedVal += parseFloat(inv.amount_paid || 0);
      dueVal += parseFloat(inv.balance_due || 0);

      // Metal Reconciliation
      const billedMetal = parseFloat(inv.total_silver_wt || 0);
      const dueMetal = parseFloat(inv.balance_silver_wt || 0);
      const receivedMetal = parseFloat(inv.received_metal || 0);
      const settledMetal = parseFloat(inv.fixed_metal_delta || 0);

      metBilled += billedMetal;
      metRecv += receivedMetal;
      metSettled += settledMetal;
      metDue += dueMetal;

      // Current Items Revenue
      currentTransactionRev += parseFloat(inv.total_amount_before_disc || 0);

      inv.items?.forEach((item: any) => {
        const amt = parseFloat(item.stout_final_amount || 0);
        const type = item.category?.Type;
        const subType = item.category?.SubType;

        // Staff Performance Logic per item
        // Ensure robust assignment of salesperson name with fallback check
        const itemStaffName = item.user?.name || item.stout_sale_executive || item.stout_user_id || inv.salesperson_name || 'Owner';
        if (!perfMap.has(itemStaffName)) perfMap.set(itemStaffName, { name: itemStaffName, soldCount: 0, achievedValue: 0, dueAmount: 0 });
        const itemStats = perfMap.get(itemStaffName);

        itemStats.soldCount++;
        itemStats.achievedValue += amt;
        // Aggregate due amount from invoice balance_due
        itemStats.dueAmount += parseFloat(inv.balance_due || 0);

        if (type === 'Master') {
          readyTotal += amt;
          if (subType === 'lot') lotTotal += amt;
          else if (subType === 'piece') pieceTotal += amt;
        }
        else if (type === 'Product') { productOnlyTotal += amt; }
        else if (type === 'Assembly') { assemblyTotal += amt; }

        // Dynamic Table Grouping
        if (type === 'Master' || type === 'Product') {
          const key = item.category?.name || 'Unknown';
          const exist = readyMap.get(key) || { name: key, amount: 0, sub: subType || 'Product' };
          exist.amount += amt; readyMap.set(key, exist);
        } else if (type === 'Assembly') {
          const key = item.category?.name || 'Unknown';
          const exist = assemblyMap.get(key) || { name: key, amount: 0 };
          exist.amount += amt; assemblyMap.set(key, exist);
        }
      });
    });

    this.totalAmountBilled = billedVal;
    this.totalAmountReceived = receivedVal;
    this.totalAmountDue = dueVal;

    this.totalMetalBilled = metBilled;
    this.totalMetalReceived = metRecv;
    this.totalMetalSettled = metSettled;
    this.totalMetalDue = metDue;
    this.totalReadyAmt = { total_amount_paid: currentTransactionRev };
    this.totalReadyProductSale = readyTotal;
    this.totalAssemblyAmt = assemblyTotal;
    this.lotWiseSale = lotTotal;
    this.pieceWiseSale = pieceTotal;
    this.productTypeSale = productOnlyTotal;
    this.staffPerformance = Array.from(perfMap.values());

    const maxVal = Math.max(lotTotal, pieceTotal, productOnlyTotal, 1);
    this.lotPercent = (lotTotal / maxVal) * 100;
    this.piecePercent = (pieceTotal / maxVal) * 100;
    this.productPercent = (productOnlyTotal / maxVal) * 100;

    this.dynamicReadyCategoryItems = Array.from(readyMap.values());
    this.dynamicAssemblyCategoryItems = Array.from(assemblyMap.values());
  }

  // --- STAFF PAGINATION GETTERS ---
  get totalStaffPages(): number { return Math.ceil(this.staffPerformance.length / this.pageSizeStaff) || 1; }
  get paginatedStaffPerformance(): any[] {
    const start = (this.currentPageStaff - 1) * this.pageSizeStaff;
    return this.staffPerformance.slice(start, start + this.pageSizeStaff);
  }
  goToStaffPage(page: number): void { if (page >= 1 && page <= this.totalStaffPages) this.currentPageStaff = page; }

  // --- SERVICE METHODS (Maintained) ---
  getFirms() { this.firmService.getFirms().subscribe((res: any) => this.firms = res.data.filter((f: any) => f.gst_status === 'non-registered')); }
  getTotalReadyProductAmount(p: HttpParams) { this.salesDashboardService.getTotalReadyProductAmount(p).subscribe(); }
  getAssemblyLineSale(p: HttpParams) { this.salesDashboardService.getTotalAssemblyProductSale(p).subscribe(); }
  getTotalReadyProductSale(p: HttpParams) { this.salesDashboardService.getTotalReadyProductSale(p).subscribe(); }
  getTopTenCustomers(p: HttpParams) {
    this.salesDashboardService.getTopTenCustomers(p).subscribe((res: any) => {
      this.topTenCustomers = res.data || [];
      this.topFourCustomers = (res.data || []).slice(0, 4);
    });
  }
  getTotalExpenses(p: HttpParams) {
    this.salesDashboardService.getTotalExpenses(p).subscribe((res: any) => this.total_expense = res.data.total_expense);
  }
  fetchNewCategories(p: HttpParams) {
    this.salesDashboardService.getNewCategories(p).subscribe((res: any) => {
      this.readyProducts = res?.data?.ready_products || [];
      this.assemblyProducts = res?.data?.assembly_products || [];
    });
  }
  fetchSalesExecutiveReport(p: HttpParams) { this.salesDashboardService.getSalesExecutiveReport(p).subscribe((res: any) => this.salesExecutives = res.data || []); }
  goToStaffTab() { this.router.navigate(['/admin-dashboard'], { queryParams: { tab: 'staff' }, queryParamsHandling: 'merge' }); }
  getStaffWiseExpense(p: HttpParams) { this.expenseService.getExpenses(p).subscribe((res: any) => { this.expenses = res.data, this.consolidateStaffWise(); }) }

  consolidateStaffWise() {

    const grouped: { [key: number]: any } = {};

    this.expenses.forEach((item: any) => {

      const user = item.user;
      if (!user) return;

      const userId = user.id;

      if (!grouped[userId]) {
        grouped[userId] = {
          user: user,
          amount: 0
        };
      }

      grouped[userId].amount += Number(item.amount);
    });

    // Convert object → array
    this.staffWiseExpenses = Object.values(grouped);

    // Optional: Top spenders first
    this.staffWiseExpenses.sort((a, b) => b.amount - a.amount);
  }
  // Add this method for the today,monthly,yearly filter
  setDateFilter(type: 'today' | 'monthly' | 'yearly') {
    this.activeFilter = type;
    const now = new Date();

    // Format helper to handle local timezone dates correctly
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD
    };

    if (type === 'today') {
      this.dateFrom = formatDate(now);
      this.dateTo = formatDate(now);
    }
    else if (type === 'monthly') {
      // First day of current month
      this.dateFrom = formatDate(new Date(now.getFullYear(), now.getMonth(), 1));
      this.dateTo = formatDate(now);
    }
    else if (type === 'yearly') {
      // First day of current year
      this.dateFrom = formatDate(new Date(now.getFullYear(), 0, 1));
      this.dateTo = formatDate(now);
    }

    this.refreshAllData();
  }
}